import { AnimatePresence, motion } from "framer-motion"
import { useMemo, useState } from "react"
import IndividualTransportIcn from "../../../assets/transfer/transports/economy.6ebfa495.png"
import GroupTransportIcn from "../../../assets/transfer/transports/minibus.22913219.png"
import PhotoSlider from "../../../packs/transfer/components/PhotoSlider/PhotoSlider"
import BookingModal from "./BookingModal"
import TransferOption from "./TransferOption"

const typeColors = {
  rent: { bg: "bg-blue-50", text: "text-blue-600", border: "border-blue-100", label: "Individual" },
  group: {
    bg: "bg-emerald-50",
    text: "text-emerald-600",
    border: "border-emerald-100",
    label: "Group",
  },
}

const TRANSPORT_ICONS = {
  bus: GroupTransportIcn,
  taxi: IndividualTransportIcn,
  speed_boat: IndividualTransportIcn,
  avia: IndividualTransportIcn,
  flight_domestic: IndividualTransportIcn,
  private_transfer: IndividualTransportIcn,
  group_transfer: GroupTransportIcn,
  no_transfer: IndividualTransportIcn,
}

const Card = ({ data, index, handleBook, passengers, transferAdditionalOptions }) => {
  const { children } = passengers

  const [isExpanded, setIsExpanded] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedPriceId, setSelectedPriceId] = useState(null)

  const isGroup = data?.transfer_type === "group"

  const badge = typeColors[data.transfer_type] || typeColors.rent
  const prices = data.prices ?? []
  const minPrice = prices.length ? Math.min(...prices.map((p) => (isGroup ? p.total : p.adult))) : 0
  const company = prices[0]?.company ?? ""
  const currency = prices[0]?.currency ?? "USD"
  const title = prices[0]?.name ?? ""
  const stations = data.stations ?? []
  const priceId = prices[0]?.id

  const transportCode = data.transport_type_code
  const fallbackIcon =
    TRANSPORT_ICONS[transportCode] ||
    (data.transfer_type === "group" ? GroupTransportIcn : IndividualTransportIcn)
  const transportIcon = data.transport_type_icon || fallbackIcon
  // const transportIcon = data.transport_type_icon || fallbackIcon

  const selectedPrice = prices.find((p) => p.id === selectedPriceId)

  const allPhotos = useMemo(() => {
    const seen = new Set()
    return prices
      .flatMap((p) => p.photos ?? [])
      .filter((url) => {
        if (seen.has(url)) return false
        seen.add(url)
        return true
      })
  }, [prices])

  const openBooking = (priceId) => {
    setSelectedPriceId(priceId)
    setModalOpen(true)
  }

  const handleConfirm = (formData) => {
    handleBook(formData)
    setModalOpen(false)
  }

  {
    /* <div className="w-24 sm:w-36 h-20 sm:h-28 shrink-0 rounded-xl bg-gradient-to-br from-bg-secondary to-surface-soft flex items-center justify-center overflow-hidden p-3">
            <img
              src={transportIcon}
              alt={data.transfer_type === "group" ? "Group transport" : "Individual transport"}
              className="w-full h-full object-contain"
              onError={(e) => {
                e.currentTarget.src = fallbackIcon
              }}
            />
          </div> */
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: index * 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="bg-white rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden"
      >
        <div className="p-4 sm:p-5 flex gap-3 sm:gap-4">
          <PhotoSlider
            photos={allPhotos}
            fallbackIcon={transportIcon}
            alt={data.transfer_type === "group" ? "Group transport" : "Individual transport"}
          />

          <div className="flex-1 min-w-0 flex flex-col">
            <div className="flex flex-wrap items-start gap-1.5 mb-1">
              <div className="min-w-0">
                <h3 className="text-app-base-strong font-semibold text-sm sm:text-base leading-tight">
                  {title}
                </h3>
              </div>
              <span
                className={`inline-flex items-center text-[10px] sm:text-xs font-medium px-2 py-0.5 rounded-full border ${badge.bg} ${badge.text} ${badge.border} shrink-0`}
              >
                {badge.label}
              </span>
              {data.transport_type && (
                <span className="inline-flex items-center text-[10px] sm:text-xs font-medium px-2 py-0.5 rounded-full border bg-gray-50 text-gray-500 border-gray-200 shrink-0">
                  {data.transport_type}
                </span>
              )}
            </div>

            <p className="text-[10px] sm:text-xs text-app-content-faint font-medium tracking-wide uppercase mb-2">
              {company}
            </p>

            {stations.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {stations.map((station) => (
                  <span
                    key={station.id}
                    className="inline-flex items-center gap-1 text-[10px] sm:text-xs font-medium px-2 py-0.5 rounded-full bg-violet-50 text-violet-600 border border-violet-100"
                  >
                    <PinIcon />
                    {station.name}
                  </span>
                ))}
              </div>
            )}

            <div className="flex flex-wrap gap-2 mb-2">
              {prices.map((p) => (
                <span
                  key={p.id}
                  className="text-xs text-app-content-subtle bg-gray-50 border border-gray-100 rounded px-2 py-0.5"
                >
                  {p.pax_from}–{p.pax_to} pax · {currency} {p.adult.toFixed(0)}
                </span>
              ))}
            </div>

            {prices[0] && (
              <p className="text-[10px] text-app-content-faint">
                Valid: {prices[0].valid_from} – {prices[0].valid_to}
              </p>
            )}
          </div>

          {/* Правая колонка — десктоп */}
          <div className="hidden sm:flex flex-col items-end justify-center shrink-0 pl-4 border-l border-gray-100 min-w-[150px] gap-3">
            <div className="text-right">
              <p className="text-xs text-app-content-faint mb-0.5">from</p>
              <p className="text-2xl font-bold text-app-content tracking-tight">
                {currency} {minPrice.toFixed(0)}
              </p>
              <p className="text-[10px] text-app-content-faint mt-0.5">
                {isGroup ? "per group" : "per person"}
              </p>
            </div>

            <motion.button
              onClick={() => openBooking(priceId)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              className="w-full py-2.5 bg-app-base-strong hover:bg-app-base text-white text-sm font-semibold rounded-xl transition-colors duration-150 shadow-sm shadow-app-base-strong/25"
            >
              Book
            </motion.button>

            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="w-full flex items-center justify-center gap-1.5 text-xs text-app-base-strong hover:text-app-base transition-colors"
            >
              {isExpanded ? "Hide options" : "Show options"}
              <motion.span
                animate={{ rotate: isExpanded ? 180 : 0 }}
                transition={{ duration: 0.25, ease: "easeInOut" }}
                className="inline-flex"
              >
                <ChevronDown />
              </motion.span>
            </button>
          </div>
        </div>

        {/* Мобильная строка цены */}
        <div className="sm:hidden flex items-center justify-between gap-3 px-4 pb-4 pt-0 border-t border-gray-50">
          <div className="flex items-baseline gap-1">
            <span className="text-[10px] text-app-content-faint">from</span>
            <span className="text-xl font-bold text-app-content">
              {currency} {minPrice.toFixed(0)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center gap-1 text-xs text-app-base-strong py-2 px-3 rounded-lg border border-app-base-strong/20 hover:bg-app-base-strong/5 transition-colors"
            >
              Options
              <motion.span
                animate={{ rotate: isExpanded ? 180 : 0 }}
                transition={{ duration: 0.25 }}
                className="inline-flex"
              >
                <ChevronDown />
              </motion.span>
            </button>
            <motion.button
              onClick={() => handleBook(priceId)}
              whileTap={{ scale: 0.97 }}
              className="py-2 px-5 bg-app-base-strong hover:bg-app-base text-white text-sm font-semibold rounded-xl transition-colors shadow-sm shadow-app-base-strong/25"
            >
              Book
            </motion.button>
          </div>
        </div>

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              key="options"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
              style={{ overflow: "hidden" }}
            >
              <div className="px-4 sm:px-5 pb-4 sm:pb-5 pt-1 border-t border-gray-100 bg-gray-50/60 flex flex-col gap-2">
                <p className="text-[10px] font-semibold text-app-content-faint uppercase tracking-wider pt-3 pb-1">
                  Select vehicle
                </p>
                {prices.map((price, idx) => (
                  <TransferOption
                    key={price.id}
                    index={idx}
                    title={price.name}
                    subtitle={`${price.pax_from}–${price.pax_to} pax`}
                    price={isGroup ? price.total : price.adult}
                    currency={currency}
                    priceChild={price.child}
                    priceInfant={price.infant}
                    passengers={price.pax_to}
                    isGroup={isGroup}
                    nonRefundable={price.non_refundable}
                    onRequest={price.on_request}
                    handleBook={() => openBooking(price.id)}
                  />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
      <BookingModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleConfirm}
        priceId={selectedPriceId}
        childrenCount={children}
        currency={currency}
        price={isGroup ? (selectedPrice?.total ?? minPrice) : (selectedPrice?.adult ?? minPrice)}
        transferType={data.transfer_type}
        additionalOptions={transferAdditionalOptions}
      />
    </>
  )
}

const ChevronDown = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
)

const PinIcon = () => (
  <svg width="10" height="10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
    />
  </svg>
)

export default Card
